import Axios from "helpers/Interceptor";
import { FbSuggestion, SpotifyArtistInBrandConnections, SpotifyArtistSearchResponse } from "modules/Types";

/**
 * Fetches artist details from Spotify API by their Spotify ID and transforms it into a standardized format
 * 
 * @param artistId - The Spotify artist ID to look up
 * @returns {Promise<SpotifyArtistInBrandConnections|null>} Spotify artist object or null if not found/error
 */
export const getBrandBySpotifyId_api = async (artistId: string): Promise<SpotifyArtistInBrandConnections | null> => {
    try {
        const { data: artist } = await Axios.get<{
            external_urls: any;
            followers: any;
            genres: string[];
            href: string;
            id: string;
            images: any[];
            name: string;
            popularity: number;
            type: string;
            uri: string;
        }>(`/spotify/artists/${artistId}`);

        if (!artist) return null;

        return {
            externalUrls: artist.external_urls,
            followers: artist.followers,
            genres: artist.genres,
            href: artist.href,
            id: artist.id,
            images: artist.images,
            name: artist.name,
            popularity: artist.popularity,
            type: artist.type,
            uri: artist.uri
        };
    } catch (err) {
        return null;
    }
};

/**
 * Searches for Spotify artists based on a query string
 * 
 * @param query - The search query string to find artists
 * @param limit - Optional limit on number of results to return (default 20)
 * @returns {Promise<SpotifyArtistSearchResponse[]>} Array of matching Spotify artists
 */
export const searchSpotifyArtists_api = async (query: string, limit: number = 20): Promise<SpotifyArtistSearchResponse[]> => {
    try {
        const response = await Axios.get<SpotifyArtistSearchResponse[]>(
            '/spotify/search/artists', {
                params: {
                    q: query,
                    limit
                }
            }
        );

        const artists: SpotifyArtistSearchResponse[] = response?.data || [];
        return artists;
    } catch (err) {
        return [];
    }
};

/**
 * Fetches related artists for a Spotify artist by their ID
 * 
 * @param spotifyId - The Spotify ID of the artist to fetch related artists for
 * @returns {Promise<SpotifyArtistSearchResponse[]>} Array of related Spotify artists
 */
export const findRelatedArtistsForSpotifyId_api = async (spotifyId: string): Promise<{
  artists: SpotifyArtistSearchResponse[],
  targetingAudiences: FbSuggestion[]
}> => {
    try {
        const response = await Axios.get(`/spotify/artists/${spotifyId}/related-artists`);
        return response.data;
    } catch (err) {
        return {
            artists: [],
            targetingAudiences: []
        };
    }
};
/** Response type for Spotify token endpoint */
interface SpotifyTokenResponse {
  token: string;
  error?: string;
}

/**
 * Fetches a fresh Spotify API token from the backend
 * 
 * @returns Promise containing the token response or error
 */
export const getNewSpotifyToken_api = async (): Promise<{ data: SpotifyTokenResponse | null; error: any }> => {
  try {
    const response = await Axios.get<SpotifyTokenResponse>('/token/spotify');
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Fetches updated streaming links for a Spotify track URL
 * 
 * @param spotifyTrackUrl - The Spotify track URL to fetch streaming links for
 * @returns Promise containing the streaming links data or error
 */
export const refreshStreamingLinks_api = async (spotifyTrackUrl: string) => {
  try {
    const response = await Axios.post('/spotify/track/streaming-links', { 
      spotifyTrackUrl 
    });
    return response.data;
  } catch (error) {
    return [];
  }
};
